import React, { useState, useCallback } from "react";
import {
  AssetType,
  AssetFormData,
  AssetStatus,
  ASSET_TYPE_LABELS,
  CreateAssetInput,
  UpdateAssetInput,
  Asset,
} from "../../types/asset-types";
import { VenueSelector } from "./venue-selector";
import { StationaryAssetForm } from "./forms/stationary-asset-form";
import { MobileAssetForm } from "./forms/mobile-asset-form";
import { PrintAssetForm } from "./forms/print-asset-form";
import { DigitalAssetForm } from "./forms/digital-asset-form";
import { SeatingAssetForm } from "./forms/seating-asset-form";
import { FormField, TextInput } from "./forms/asset-form-base";

export interface AssetFormProps {
  initialData?: Partial<Asset>;
  onSubmit: (data: CreateAssetInput | UpdateAssetInput) => Promise<void>;
  onCancel?: () => void;
  isEditing?: boolean;
  disabled?: boolean;
  title?: string;
}

export function AssetForm({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
  disabled = false,
  title,
}: AssetFormProps) {
  // Clean initial metadata by removing GraphQL response fields
  const cleanInitialMetadata = initialData?.metadata
    ? Object.fromEntries(
        Object.entries(initialData.metadata).filter(
          ([key, value]) =>
            key !== "__typename" && value !== null && value !== undefined
        )
      )
    : {};

  const [formData, setFormData] = useState<AssetFormData>({
    name: initialData?.name || "",
    type: initialData?.type || AssetType.STATIONARY,
    venueId: initialData?.venue?.id,
    venue: initialData?.venue,
    metadata: cleanInitialMetadata,

    bulkConfig: {
      enabled: false,
      count: 1,
      namingPattern: "",
      variableMetadata: [],
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle form data changes
  const handleFormDataChange = useCallback((newData: AssetFormData) => {
    setFormData(newData);
    // Clear errors for changed fields
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith("metadata.")) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  }, []);

  // Handle asset type change
  const handleTypeChange = (type: AssetType) => {
    setFormData((prev) => ({
      ...prev,
      type,
      metadata: {}, // Reset metadata when type changes
    }));
    setErrors({});
  };

  // Handle venue selection
  const handleVenueSelect = (venue: any) => {
    setFormData((prev) => ({
      ...prev,
      venueId: venue.id,
      venue,
    }));
    if (errors.venueId) {
      setErrors((prev) => ({ ...prev, venueId: "" }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic validation
    if (!formData.name.trim()) {
      newErrors.name = "Asset name is required";
    }

    // Type-specific validation
    switch (formData.type) {
      case AssetType.STATIONARY:
        const stationaryMeta = formData.metadata as any;
        // Coordinates are now completely optional
        // Validate ranges if provided
        if (
          stationaryMeta.latitude !== undefined &&
          stationaryMeta.latitude !== null &&
          stationaryMeta.latitude !== ""
        ) {
          if (stationaryMeta.latitude < -90 || stationaryMeta.latitude > 90) {
            newErrors["metadata.latitude"] =
              "Latitude must be between -90 and 90";
          }
        }
        if (
          stationaryMeta.longitude !== undefined &&
          stationaryMeta.longitude !== null &&
          stationaryMeta.longitude !== ""
        ) {
          if (
            stationaryMeta.longitude < -180 ||
            stationaryMeta.longitude > 180
          ) {
            newErrors["metadata.longitude"] =
              "Longitude must be between -180 and 180";
          }
        }
        if (
          stationaryMeta.elevation !== undefined &&
          stationaryMeta.elevation !== null &&
          stationaryMeta.elevation !== ""
        ) {
          if (
            stationaryMeta.elevation < -1000 ||
            stationaryMeta.elevation > 10000
          ) {
            newErrors["metadata.elevation"] =
              "Elevation must be between -1000 and 10000 meters";
          }
        }
        if (!stationaryMeta.locationDescription?.trim()) {
          newErrors["metadata.locationDescription"] =
            "Location description is required";
        }
        break;

      case AssetType.MOBILE:
        const mobileMeta = formData.metadata as any;
        if (!mobileMeta.vehicleId?.trim()) {
          newErrors["metadata.vehicleId"] = "Vehicle ID is required";
        }
        if (!mobileMeta.vehicleType?.trim()) {
          newErrors["metadata.vehicleType"] = "Vehicle type is required";
        }
        if (!mobileMeta.operator?.trim()) {
          newErrors["metadata.operator"] = "Operator is required";
        }
        if (!mobileMeta.trackingMethod?.trim()) {
          newErrors["metadata.trackingMethod"] = "Tracking method is required";
        }
        break;

      case AssetType.PRINT:
        const printMeta = formData.metadata as any;
        if (!printMeta.publication?.trim()) {
          newErrors["metadata.publication"] = "Publication is required";
        }
        if (!printMeta.publicationType?.trim()) {
          newErrors["metadata.publicationType"] =
            "Publication type is required";
        }
        if (!printMeta.adSize?.trim()) {
          newErrors["metadata.adSize"] = "Ad size is required";
        }
        break;

      case AssetType.DIGITAL:
        const digitalMeta = formData.metadata as any;
        if (!digitalMeta.platform?.trim()) {
          newErrors["metadata.platform"] = "Platform is required";
        }
        if (!digitalMeta.platformType?.trim()) {
          newErrors["metadata.platformType"] = "Platform type is required";
        }
        if (!digitalMeta.placement?.trim()) {
          newErrors["metadata.placement"] = "Placement is required";
        }
        break;

      case AssetType.SEATING:
        const seatingMeta = formData.metadata as any;
        if (!seatingMeta.section?.trim()) {
          newErrors["metadata.section"] = "Section is required";
        }
        if (!seatingMeta.level?.trim()) {
          newErrors["metadata.level"] = "Level is required";
        }
        if (!seatingMeta.seatStart?.trim()) {
          newErrors["metadata.seatStart"] = "Seat start is required";
        }
        if (!seatingMeta.seatEnd?.trim()) {
          newErrors["metadata.seatEnd"] = "Seat end is required";
        }
        if (!seatingMeta.seatCount || seatingMeta.seatCount < 1) {
          newErrors["metadata.seatCount"] = "Seat count must be at least 1";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    const submissionId = `submission-${Date.now()}-${Math.random()}`;
    console.log(`🚀 [${submissionId}] Form submission started`, {
      timestamp: new Date().toISOString(),
      isSubmitting,
      formData: { name: formData.name, type: formData.type },
    });

    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) {
      console.log(
        `⚠️ [${submissionId}] Form already submitting, ignoring duplicate submission`
      );
      return;
    }

    if (!validateForm()) {
      console.log(`❌ [${submissionId}] Form validation failed`);
      return;
    }

    console.log(`🔒 [${submissionId}] Setting isSubmitting to true`);
    setIsSubmitting(true);

    try {
      // Clean metadata by removing null values and __typename
      const cleanMetadata = Object.fromEntries(
        Object.entries(formData.metadata).filter(([key, value]) => {
          // Skip __typename and null/undefined/empty values
          if (
            key === "__typename" ||
            value === null ||
            value === undefined ||
            value === ""
          ) {
            return false;
          }
          return true;
        })
      );

      // Also clean any nested __typename fields
      Object.keys(cleanMetadata).forEach((key) => {
        if (
          typeof cleanMetadata[key] === "object" &&
          cleanMetadata[key] !== null
        ) {
          const nestedObj = cleanMetadata[key] as any;
          if ("__typename" in nestedObj) {
            delete nestedObj.__typename;
          }
        }
      });

      // Prepare metadata - nest under the specific asset type
      const metadataInput = {
        [formData.type.toLowerCase()]: cleanMetadata,
      };

      let submitData;
      console.log(
        "Form submission - isEditing:",
        isEditing,
        "initialData:",
        initialData
      );
      if (isEditing) {
        submitData = {
          name: formData.name,
          type: formData.type,
          metadata: metadataInput,
          venueId: formData.venueId,
        } as UpdateAssetInput;
        console.log("Prepared UpdateAssetInput:", submitData);
      } else {
        submitData = {
          name: formData.name,
          type: formData.type,
          metadata: metadataInput,
          venueId: formData.venueId,
          bulkConfig: formData.bulkConfig?.enabled
            ? {
                count: formData.bulkConfig.count,
                namingPattern: formData.bulkConfig.namingPattern,
                variableMetadata: formData.bulkConfig.variableMetadata,
              }
            : undefined,
        } as CreateAssetInput;
        console.log("Prepared CreateAssetInput:", submitData);
      }

      console.log(
        `📤 [${submissionId}] Calling onSubmit with data:`,
        submitData
      );
      await onSubmit(submitData);
      console.log(`✅ [${submissionId}] onSubmit completed successfully`);
    } catch (error) {
      console.error(`💥 [${submissionId}] Form submission error:`, error);
      // Handle error - could set a general error state here
    } finally {
      console.log(`🔓 [${submissionId}] Setting isSubmitting to false`);
      setIsSubmitting(false);
    }
  };

  // Render type-specific form
  const renderTypeSpecificForm = () => {
    const props = {
      data: formData,
      onChange: handleFormDataChange,
      errors,
      disabled: disabled || isSubmitting,
    };

    switch (formData.type) {
      case AssetType.STATIONARY:
        return <StationaryAssetForm {...props} />;
      case AssetType.MOBILE:
        return <MobileAssetForm {...props} />;
      case AssetType.PRINT:
        return <PrintAssetForm {...props} />;
      case AssetType.DIGITAL:
        return <DigitalAssetForm {...props} />;
      case AssetType.SEATING:
        return <SeatingAssetForm {...props} />;
      default:
        return null;
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      data-testid="asset-form"
    >
      {/* Form Header */}
      {title && (
        <div className="border-b border-gray-200 pb-4">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
      )}

      {/* Basic Asset Information */}
      <div className="space-y-4">
        <FormField label="Asset Name" required error={errors.name}>
          <TextInput
            value={formData.name}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, name: value }))
            }
            placeholder="Enter asset name"
            disabled={disabled || isSubmitting}
            required
            data-testid="asset-name-input"
          />
        </FormField>

        {!isEditing && (
          <FormField label="Asset Type" required>
            <div
              className="grid grid-cols-2 md:grid-cols-5 gap-3"
              data-testid="asset-type-selector"
            >
              {Object.values(AssetType).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeChange(type)}
                  disabled={disabled || isSubmitting}
                  className={`p-3 border-2 rounded-lg text-center transition-colors ${
                    formData.type === type
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-300 hover:border-gray-400"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`asset-type-${type.toLowerCase()}`}
                >
                  <div className="text-sm font-medium">
                    {ASSET_TYPE_LABELS[type]}
                  </div>
                </button>
              ))}
            </div>
          </FormField>
        )}

        <VenueSelector
          selectedVenue={formData.venue}
          onVenueSelect={handleVenueSelect}
          disabled={disabled || isSubmitting}
          error={errors.venueId}
        />
      </div>

      {/* Type-Specific Form */}
      {renderTypeSpecificForm()}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cancel-button"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={disabled || isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-button"
        >
          {isSubmitting
            ? isEditing
              ? "Updating..."
              : "Creating..."
            : isEditing
            ? "Update Asset"
            : "Create Asset"}
        </button>
      </div>
    </form>
  );
}
